import { useEffect, useState, useRef } from "react";
import { Instagram, Facebook, Phone, Mail, MessageCircle, ArrowUp, Download, X, ChevronLeft, ChevronRight, Expand } from "lucide-react";
import "./App.css";

// Dimensione icone per i bottoni CTA principali (Contatti)
const CTA_ICON_SIZE = 22;

// Tracciamento eventi Google Analytics (GA4) sui CTA
const trackCta = (action, location) => {
  if (typeof window !== "undefined" && typeof window.gtag === "function") {
    window.gtag("event", action, { event_category: "cta", event_label: location });
  }
};

// =========================================
// CONFIGURAZIONE - Modifica qui i contenuti
// =========================================
const CONFIG = {
  brand: {
    name: "DJ Pier",
    fullName: "Pierluigi Venturini",
    tagline: "Music • Events • Direction",
    heroSubtext: "DJ per club ed eventi privati",
  },
  contacts: {
    instagram: "https://www.instagram.com/djpier_/",
    facebook: "https://m.facebook.com/djpierventurini",
    whatsapp: "https://wa.me/393450091149",
    phone: "tel:+393450091149",
    phoneDisplay: "+39 345 009 1149",
    email: "venturinipierluigi@gmail.com",
    pressKit: "https://bit.ly/4o4pGv1",
  },
  microcopy: {
    availability: "Disponibile Centro Italia",
    quickQuote: "Preventivo rapido su WhatsApp",
    ctaPrimary: "Scrivimi su WhatsApp",
    ctaSecondary: "Instagram",
    ctaPressKit: "Download Press Kit",
  },
  services: [
    {
      id: 1,
      title: "Club DJ",
      subtitle: "Techno • House • Commercial",
      description: "Set su misura per ogni pista. Dalla techno underground al mainstream che fa cantare tutti.",
      image: "/img/service-1.webp",
    },
    {
      id: 2,
      title: "Eventi Privati",
      subtitle: "Wedding • Corporate • Luxury",
      description: "Dal primo ballo al party finale. Regia musicale completa per il tuo evento speciale.",
      image: "/img/service-2.webp",
    },
    {
      id: 3,
      title: "Regia Serata",
      subtitle: "Sound • Light • Experience",
      description: "Non solo musica. Coordino luci, tempi e atmosfera per creare momenti indimenticabili.",
      image: "/img/service-3.webp",
    },
  ],
  gallery: [
    { id: 1, src: "/img/gallery-1.webp", alt: "DJ Pier live - luci viola" },
    { id: 2, src: "/img/gallery-2.webp", alt: "Serata in console" },
    { id: 3, src: "/img/gallery-3.webp", alt: "Atmosfera club" },
    { id: 4, src: "/img/gallery-4.webp", alt: "DJ set notturno" },
    { id: 5, src: "/img/gallery-5.webp", alt: "Pubblico in pista" },
    { id: 6, src: "/img/gallery-6.webp", alt: "Crowd Geko Village" },
    { id: 7, src: "/img/gallery-7.webp", alt: "Folla in festa" },
    { id: 8, src: "/img/gallery-8.webp", alt: "Energia sulla pista" },
    { id: 9, src: "/img/gallery-9.webp", alt: "Live performance" },
    { id: 10, src: "/img/gallery-10.webp", alt: "Party notturno" },
    { id: 11, src: "/img/gallery-11.webp", alt: "DJ Pier alla console Pioneer" },
    { id: 12, src: "/img/gallery-12.webp", alt: "DJ Pier in cabina" },
    { id: 13, src: "/img/gallery-13.webp", alt: "DJ Pier mixing" },
    { id: 14, src: "/img/gallery-14.webp", alt: "DJ set beach party" },
    { id: 15, src: "/img/gallery-15.webp", alt: "Regia serata Bagni Riviera" },
  ],
  reviews: [
    // Sposi
    { id: 1, text: "Non volevamo il classico DJ \"urlato\". Pier è stato elegante, preciso e sempre sul pezzo. Anche gli invitati più difficili si sono divertiti. Scelta che rifaremmo senza pensarci.", author: "Alessandro & Federica", event: "Wedding" },
    { id: 2, text: "Grande attenzione ai dettagli e ottima gestione dei tempi. Ha rispettato ogni momento importante senza forzare nulla. La festa finale è stata esattamente come la immaginavamo.", author: "Luca & Martina", event: "Wedding" },
    // Club Owner
    { id: 3, text: "Pier è affidabile, puntuale e sa leggere il pubblico. Non suona per sé stesso ma per la pista, ed è una qualità che oggi non è scontata. Ottima collaborazione.", author: "Andrea R.", event: "Club Manager" },
    { id: 4, text: "Ha uno stile riconoscibile ma adattabile. Sa tenere alta l'energia senza strafare. Tecnica solida e mentalità professionale.", author: "Davide C.", event: "Direzione Artistica" },
    { id: 5, text: "Con Pier sai cosa aspettarti: serata ordinata, pubblico coinvolto e zero problemi. È uno di quei DJ che lavorano bene anche dietro le quinte.", author: "Stefano M.", event: "Club Owner" },
    // Eventi Privati
    { id: 6, text: "Ci serviva una musica che accompagnasse l'evento senza rubare la scena. Pier ha gestito tutto con grande equilibrio, adattandosi perfettamente ai diversi momenti della serata.", author: "Responsabile Organizzazione", event: "Evento Aziendale" },
    { id: 7, text: "Ha saputo mescolare gusti diversi senza creare confusione. Tutti si sono sentiti coinvolti, dai più giovani ai meno giovani. Molto professionale.", author: "Cliente Privato", event: "Compleanno 40 anni" },
    { id: 8, text: "Musica curata, volume sempre corretto e atmosfera centrata. È raro trovare qualcuno così attento al contesto.", author: "Ospite", event: "Evento Privato su Invito" },
  ],
};

// Hero Section
function Hero() {
  const [loaded, setLoaded] = useState(false);
  useEffect(() => { setLoaded(true); }, []);

  return (
    <section data-testid="hero-section" className="relative h-screen flex flex-col justify-center items-center overflow-hidden">
      <div className="absolute inset-0 z-0">
        {/* Sfondo sfocato che riempie lo schermo */}
        <img src="/img/hero.webp" alt="" aria-hidden="true" className="absolute inset-0 w-full h-full object-cover blur-2xl scale-110 opacity-50" />
        {/* Immagine completa, centrata e non tagliata */}
        <img src="/img/hero.webp" alt="DJ Pier" fetchpriority="high" className="absolute inset-0 w-full h-full object-contain object-center" />
        <div className="absolute inset-0 bg-black/50" />
      </div>
      <div className={`relative z-10 text-center px-6 transition-all duration-1000 ${loaded ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'}`}>
        <img 
          src="/img/logo.webp" 
          alt="DJ Pier Logo" 
          width="500" height="353"
          className="w-[280px] sm:w-[380px] md:w-[500px] mx-auto mb-6"
        />
        <p className="text-sm sm:text-base md:text-lg tracking-[0.3em] uppercase text-neutral-400 mb-8">{CONFIG.brand.tagline}</p>
        <p className="text-neutral-400 text-base md:text-lg mb-12 max-w-md mx-auto">{CONFIG.brand.heroSubtext}</p>
        <div className="flex flex-col sm:flex-row gap-4 justify-center">
          <a href={CONFIG.contacts.whatsapp} onClick={() => trackCta("whatsapp_click", "hero")} target="_blank" rel="noopener noreferrer" data-testid="hero-whatsapp-btn" className="inline-flex items-center justify-center gap-3 bg-white text-black px-8 py-4 font-semibold text-sm uppercase tracking-wider hover:bg-neutral-200 transition-colors duration-300">
            <MessageCircle size={20} />{CONFIG.microcopy.ctaPrimary}
          </a>
          <a href={CONFIG.contacts.instagram} target="_blank" rel="noopener noreferrer" data-testid="hero-instagram-btn" className="inline-flex items-center justify-center gap-3 bg-transparent border border-white text-white px-8 py-4 font-semibold text-sm uppercase tracking-wider hover:bg-white hover:text-black transition-all duration-300">
            <Instagram size={20} />{CONFIG.microcopy.ctaSecondary}
          </a>
          {CONFIG.contacts.pressKit && (
            <a href={CONFIG.contacts.pressKit} onClick={() => trackCta("presskit_download", "hero")} target="_blank" rel="noopener noreferrer" data-testid="hero-presskit-btn" className="inline-flex items-center justify-center gap-3 bg-transparent border border-neutral-600 text-neutral-300 px-8 py-4 font-semibold text-sm uppercase tracking-wider hover:border-white hover:text-white transition-all duration-300">
              <Download size={20} />{CONFIG.microcopy.ctaPressKit}
            </a>
          )}
        </div>
      </div>
      <div className={`absolute bottom-8 left-1/2 -translate-x-1/2 z-10 transition-opacity duration-1000 delay-1000 ${loaded ? 'opacity-100' : 'opacity-0'}`}>
        <div className="w-6 h-10 border-2 border-white/30 rounded-full flex justify-center pt-2">
          <div className="w-1 h-2 bg-white rounded-full animate-bounce" />
        </div>
      </div>
    </section>
  );
}

// Social Hub Section
function SocialHub() {
  const ref = useRef(null);
  const [isVisible, setIsVisible] = useState(false);
  useEffect(() => {
    const observer = new IntersectionObserver(([entry]) => { if (entry.isIntersecting) setIsVisible(true); }, { threshold: 0.1 });
    if (ref.current) observer.observe(ref.current);
    return () => observer.disconnect();
  }, []);

  const socials = [
    { icon: Instagram, label: "Instagram", href: CONFIG.contacts.instagram, testId: "social-instagram" },
    { icon: Facebook, label: "Facebook", href: CONFIG.contacts.facebook, testId: "social-facebook" },
    { icon: MessageCircle, label: "WhatsApp", href: CONFIG.contacts.whatsapp, testId: "social-whatsapp", action: "whatsapp_click" },
  ];

  return (
    <section ref={ref} data-testid="social-hub-section" className="py-24 md:py-32 px-6 bg-neutral-950">
      <div className="max-w-screen-xl mx-auto">
        <div className={`grid grid-cols-1 md:grid-cols-3 gap-4 md:gap-6 transition-all duration-700 ${isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'}`}>
          {socials.map((social) => (
            <a key={social.label} href={social.href} onClick={() => social.action && trackCta(social.action, "social-hub")} target="_blank" rel="noopener noreferrer" data-testid={social.testId} className="group flex flex-col items-center justify-center p-12 md:p-16 border border-neutral-800 bg-black hover:bg-white hover:border-white transition-all duration-300">
              <social.icon size={48} className="text-white group-hover:text-black transition-colors duration-300 mb-4" />
              <span className="text-sm uppercase tracking-widest text-neutral-400 group-hover:text-black transition-colors duration-300">{social.label}</span>
            </a>
          ))}
        </div>
      </div>
    </section>
  );
}

// Services Section
function Services() {
  const ref = useRef(null);
  const [isVisible, setIsVisible] = useState(false);
  useEffect(() => {
    const observer = new IntersectionObserver(([entry]) => { if (entry.isIntersecting) setIsVisible(true); }, { threshold: 0.1 });
    if (ref.current) observer.observe(ref.current);
    return () => observer.disconnect();
  }, []);

  return (
    <section ref={ref} data-testid="services-section" className="py-24 md:py-32 px-6 bg-black">
      <div className="max-w-screen-xl mx-auto">
        <div className={`transition-all duration-700 ${isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'}`}>
          <h2 className="font-heading text-4xl md:text-6xl font-bold tracking-tight text-center mb-4 text-white">Cosa Faccio</h2>
          <p className="text-neutral-400 text-center mb-16 max-w-lg mx-auto">{CONFIG.microcopy.availability}</p>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            {CONFIG.services.map((service) => (
              <div key={service.id} data-testid={`service-card-${service.id}`} className="group bg-neutral-950 border border-neutral-800 overflow-hidden hover:border-white/30 transition-all duration-300">
                <div className="relative h-64 overflow-hidden">
                  <img src={service.image} alt={service.title} loading="lazy" className="w-full h-full object-cover object-top group-hover:scale-105 transition-all duration-500" />
                  <div className="absolute inset-0 bg-black/30 group-hover:bg-black/10 transition-colors duration-300" />
                </div>
                <div className="p-6 md:p-8">
                  <h3 className="font-heading text-2xl md:text-3xl font-semibold mb-2 text-white">{service.title}</h3>
                  <p className="text-xs uppercase tracking-widest text-neutral-500 mb-4">{service.subtitle}</p>
                  <p className="text-neutral-400 text-sm leading-relaxed">{service.description}</p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>
    </section>
  );
}

// Lightbox overlay (mounted only while a photo is open)
function Lightbox({ images, index, onClose, onPrev, onNext }) {
  useEffect(() => {
    const onKey = (e) => {
      if (e.key === "Escape") onClose();
      else if (e.key === "ArrowLeft") onPrev();
      else if (e.key === "ArrowRight") onNext();
    };
    window.addEventListener("keydown", onKey);
    document.body.style.overflow = "hidden";
    return () => {
      window.removeEventListener("keydown", onKey);
      document.body.style.overflow = "";
    };
  }, [onClose, onPrev, onNext]);

  const photo = images[index];
  const sideBtn = "absolute z-10 w-12 h-12 flex items-center justify-center text-white/80 hover:text-white hover:bg-white/10 rounded-full transition-colors";

  return (
    <div data-testid="lightbox-overlay" onClick={onClose} className="fixed inset-0 z-[9999] bg-black/95 backdrop-blur-sm flex items-center justify-center p-4 sm:p-8 animate-[fadeIn_0.2s_ease]">
      <button onClick={onClose} data-testid="lightbox-close" aria-label="Chiudi" className={`${sideBtn} top-5 right-5`}>
        <X size={28} />
      </button>
      <button onClick={(e) => { e.stopPropagation(); onPrev(); }} data-testid="lightbox-prev" aria-label="Foto precedente" className={`${sideBtn} left-2 sm:left-6`}>
        <ChevronLeft size={32} />
      </button>
      <figure onClick={(e) => e.stopPropagation()} className="max-w-5xl w-full flex flex-col items-center">
        <img src={photo.src} alt={photo.alt} data-testid="lightbox-image" className="max-h-[80vh] w-auto max-w-full object-contain select-none" />
        <figcaption className="mt-4 text-sm text-neutral-400 tracking-wide">{index + 1} / {images.length}</figcaption>
      </figure>
      <button onClick={(e) => { e.stopPropagation(); onNext(); }} data-testid="lightbox-next" aria-label="Foto successiva" className={`${sideBtn} right-2 sm:right-6`}>
        <ChevronRight size={32} />
      </button>
    </div>
  );
}

// Gallery Section
function Gallery() {
  const ref = useRef(null);
  const [isVisible, setIsVisible] = useState(false);
  const [lightboxIndex, setLightboxIndex] = useState(null);
  const total = CONFIG.gallery.length;

  useEffect(() => {
    const observer = new IntersectionObserver(([entry]) => { if (entry.isIntersecting) setIsVisible(true); }, { threshold: 0.1 });
    if (ref.current) observer.observe(ref.current);
    return () => observer.disconnect();
  }, []);

  return (
    <section ref={ref} data-testid="gallery-section" className="py-24 md:py-32 px-6 bg-neutral-950">
      <div className="max-w-screen-xl mx-auto">
        <div className={`transition-all duration-700 ${isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'}`}>
          <h2 className="font-heading text-4xl md:text-6xl font-bold tracking-tight text-center mb-16 text-white">Highlights</h2>
          <div className="grid grid-cols-2 md:grid-cols-3 gap-3 md:gap-4">
            {CONFIG.gallery.map((item, index) => (
              <button
                key={item.id}
                type="button"
                onClick={() => setLightboxIndex(index)}
                data-testid={`gallery-item-${item.id}`}
                aria-label={`Apri foto: ${item.alt}`}
                className="group relative overflow-hidden block cursor-pointer focus:outline-none focus:ring-2 focus:ring-white/60"
              >
                <img src={item.src} alt={item.alt} loading="lazy" className="w-full h-48 md:h-64 object-cover group-hover:scale-105 transition-transform duration-500" />
                <div className="absolute inset-0 bg-black/0 group-hover:bg-black/40 transition-colors duration-300 flex items-center justify-center">
                  <Expand size={28} className="text-white opacity-0 group-hover:opacity-100 scale-90 group-hover:scale-100 transition-all duration-300" />
                </div>
              </button>
            ))}
          </div>
        </div>
      </div>

      {lightboxIndex !== null && (
        <Lightbox
          images={CONFIG.gallery}
          index={lightboxIndex}
          onClose={() => setLightboxIndex(null)}
          onPrev={() => setLightboxIndex((i) => (i - 1 + total) % total)}
          onNext={() => setLightboxIndex((i) => (i + 1) % total)}
        />
      )}
    </section>
  );
}

// Reviews Section
function Reviews() {
  const ref = useRef(null);
  const [isVisible, setIsVisible] = useState(false);
  const [activeIndex, setActiveIndex] = useState(0);
  
  useEffect(() => {
    const observer = new IntersectionObserver(([entry]) => { if (entry.isIntersecting) setIsVisible(true); }, { threshold: 0.1 });
    if (ref.current) observer.observe(ref.current);
    return () => observer.disconnect();
  }, []);

  useEffect(() => {
    const interval = setInterval(() => { setActiveIndex((prev) => (prev + 1) % CONFIG.reviews.length); }, 5000);
    return () => clearInterval(interval);
  }, []);

  return (
    <section ref={ref} data-testid="reviews-section" className="py-24 md:py-32 px-6 bg-black">
      <div className="max-w-screen-xl mx-auto">
        <div className={`transition-all duration-700 ${isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'}`}>
          <h2 className="font-heading text-4xl md:text-6xl font-bold tracking-tight text-center mb-16 text-white">Dicono di Me</h2>
          <div className="relative max-w-3xl mx-auto">
            <div className="text-6xl md:text-8xl text-neutral-800 font-heading absolute -top-8 left-0">"</div>
            <div className="text-center py-8 px-8 md:px-16">
              <p data-testid={`review-text-${activeIndex}`} className="text-lg md:text-2xl text-neutral-200 leading-relaxed mb-8 italic transition-opacity duration-500">{CONFIG.reviews[activeIndex].text}</p>
              <p className="text-sm uppercase tracking-widest text-neutral-400">{CONFIG.reviews[activeIndex].author}</p>
              <p className="text-xs text-neutral-500 mt-1">{CONFIG.reviews[activeIndex].event}</p>
            </div>
            <div className="text-6xl md:text-8xl text-neutral-800 font-heading absolute -bottom-8 right-0 rotate-180">"</div>
            <div className="flex justify-center gap-3 mt-8">
              {CONFIG.reviews.map((review, index) => (
                <button key={review.id} onClick={() => setActiveIndex(index)} data-testid={`review-dot-${index}`} className={`h-2 rounded-full transition-all duration-300 ${index === activeIndex ? 'bg-white w-8' : 'bg-neutral-700 w-2 hover:bg-neutral-500'}`} aria-label={`Vai alla recensione ${index + 1}`} />
              ))}
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}

// Contact Section
function Contact() {
  const ref = useRef(null);
  const [isVisible, setIsVisible] = useState(false);
  useEffect(() => {
    const observer = new IntersectionObserver(([entry]) => { if (entry.isIntersecting) setIsVisible(true); }, { threshold: 0.1 });
    if (ref.current) observer.observe(ref.current);
    return () => observer.disconnect();
  }, []);

  return (
    <section ref={ref} data-testid="contact-section" className="py-24 md:py-32 px-6 bg-neutral-950">
      <div className="max-w-screen-xl mx-auto">
        <div className={`text-center transition-all duration-700 ${isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'}`}>
          <h2 className="font-heading text-4xl md:text-6xl font-bold tracking-tight mb-4 text-white">Contattami</h2>
          <p className="text-neutral-400 mb-4 max-w-lg mx-auto">{CONFIG.microcopy.quickQuote}</p>
          <p className="text-neutral-500 text-sm mb-12">{CONFIG.microcopy.availability}</p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center mb-16">
            <a href={CONFIG.contacts.whatsapp} onClick={() => trackCta("whatsapp_click", "contatti")} target="_blank" rel="noopener noreferrer" data-testid="contact-whatsapp-btn" className="inline-flex items-center justify-center gap-3 bg-white text-black px-10 py-5 font-semibold text-sm uppercase tracking-wider hover:bg-neutral-200 transition-colors duration-300">
              <MessageCircle size={CTA_ICON_SIZE} />{CONFIG.microcopy.ctaPrimary}
            </a>
            <a href={CONFIG.contacts.phone} data-testid="contact-phone-btn" className="inline-flex items-center justify-center gap-3 bg-transparent border border-white text-white px-10 py-5 font-semibold text-sm uppercase tracking-wider hover:bg-white hover:text-black transition-all duration-300">
              <Phone size={22} />Chiamami
            </a>
          </div>
          <div className="flex flex-col sm:flex-row gap-8 justify-center items-center text-neutral-400">
            <a href={`mailto:${CONFIG.contacts.email}`} data-testid="contact-email-link" className="flex items-center gap-2 hover:text-white transition-colors">
              <Mail size={18} /><span className="text-sm">{CONFIG.contacts.email}</span>
            </a>
            <a href={CONFIG.contacts.phone} data-testid="contact-phone-link" className="flex items-center gap-2 hover:text-white transition-colors">
              <Phone size={18} /><span className="text-sm">{CONFIG.contacts.phoneDisplay}</span>
            </a>
          </div>
        </div>
      </div>
    </section>
  );
}

// Footer
function Footer() {
  return (
    <footer data-testid="footer" className="py-8 px-6 bg-black border-t border-neutral-800">
      <div className="max-w-screen-xl mx-auto flex flex-col sm:flex-row justify-between items-center gap-4">
        <p className="text-neutral-400 text-sm">© {new Date().getFullYear()} {CONFIG.brand.name} • {CONFIG.brand.fullName}</p>
        <div className="flex gap-6">
          <a href={CONFIG.contacts.instagram} target="_blank" rel="noopener noreferrer" data-testid="footer-instagram" className="text-neutral-400 hover:text-white transition-colors" aria-label="Instagram"><Instagram size={20} /></a>
          <a href={CONFIG.contacts.facebook} target="_blank" rel="noopener noreferrer" data-testid="footer-facebook" className="text-neutral-400 hover:text-white transition-colors" aria-label="Facebook"><Facebook size={20} /></a>
        </div>
      </div>
    </footer>
  );
}

// Sticky Bar
function StickyBar() {
  const [isVisible, setIsVisible] = useState(false);
  useEffect(() => {
    const handleScroll = () => { setIsVisible(window.scrollY > 500); };
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  if (!isVisible) return null;
  return (
    <div data-testid="sticky-bar" className="fixed bottom-0 left-0 right-0 z-50 md:hidden p-4 bg-black/90 backdrop-blur-xl border-t border-neutral-800">
      <div className="flex gap-3">
        <a href={CONFIG.contacts.whatsapp} onClick={() => trackCta("whatsapp_click", "sticky")} target="_blank" rel="noopener noreferrer" data-testid="sticky-whatsapp-btn" className="flex-1 inline-flex items-center justify-center gap-2 bg-white text-black py-3 font-semibold text-sm uppercase tracking-wider">
          <MessageCircle size={18} />WhatsApp
        </a>
        <a href={CONFIG.contacts.instagram} target="_blank" rel="noopener noreferrer" data-testid="sticky-instagram-btn" className="flex-1 inline-flex items-center justify-center gap-2 bg-transparent border border-white text-white py-3 font-semibold text-sm uppercase tracking-wider">
          <Instagram size={18} />Instagram
        </a>
      </div>
    </div>
  );
}

// Scroll to Top
function ScrollToTop() {
  const [isVisible, setIsVisible] = useState(false);
  useEffect(() => {
    const handleScroll = () => { setIsVisible(window.scrollY > 1000); };
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  if (!isVisible) return null;
  return (
    <button onClick={() => window.scrollTo({ top: 0, behavior: 'smooth' })} data-testid="scroll-to-top-btn" className="fixed bottom-24 md:bottom-8 right-52 z-[9998] w-12 h-12 bg-neutral-900 border border-neutral-700 flex items-center justify-center text-white hover:bg-white hover:text-black transition-all duration-300" aria-label="Torna in cima">
      <ArrowUp size={20} />
    </button>
  );
}

// Main App
function App() {
  return (
    <div className="bg-black min-h-screen">
      <Hero />
      <SocialHub />
      <Services />
      <Gallery />
      <Reviews />
      <Contact />
      <Footer />
      <StickyBar />
      <ScrollToTop />
    </div>
  );
}

export default App;
